import { CheckCircle2, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Brand = {
  name: string;
  spec: string;
  note: string;
  expertise: string[];
};
const BRANDS: Brand[] = [{
  name: "B&D Panelift",
  spec: "Most popular AU",
  note: "Australia's #1 sectional door. We stock every common profile — Icon, Classic, Statesman, Vogue — in primary Colorbond colours.",
  expertise: ["Icon ribbed profile", "Classic woodgrain", "Statesman & Vogue"]
}, {
  name: "Steel-Line",
  spec: "Premium Aus.",
  note: "Australian-made premium panels with heavier-gauge steel. We stock Therma-Range insulated panels and standard ribbed profiles.",
  expertise: ["Therma-Range insulated", "Standard ribbed profiles", "Custom colour matching"]
}, {
  name: "Centurion",
  spec: "Quality mid-range",
  note: "Common in newer Sydney builds. Vantage, Boss, and Eco-Plus panels in stock for fast turnaround.",
  expertise: ["Vantage & Boss range", "Eco-Plus insulated", "Multi-colour stock"]
}, {
  name: "Gliderol",
  spec: "Architectural",
  note: "Premium architect-spec panels common in luxury Sydney homes. R-Series and RDS-Series panels sourced within 5–7 days.",
  expertise: ["R-Series / RDS-Series", "Premium finish matching", "Architectural profiles"]
}, {
  name: "Hörmann",
  spec: "European import",
  note: "German-engineered panels with premium finishes. LPU and LTE ranges sourced through OEM channels with proper documentation.",
  expertise: ["LPU 42 panels", "LTE 42 panels", "European spec matching"]
}, {
  name: "Other / Custom",
  spec: "Any brand",
  note: "Dominator, 4Ddoors, Newgate, Superlift, Eco-Doors, plus discontinued and legacy panels. If we don't have it in stock, we source within 48 hours.",
  expertise: ["Legacy panel sourcing", "Discontinued profiles", "48-hour source guarantee"]
}];
export default function PanelBrands() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      left: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        BRANDS
      </div>

      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      right: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 72,
        alignItems: "end"
      }} className="panel-brands-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                03 / Panels we stock
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              150+ panels in stock,{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                colour matched
              </span>
              <br />
              ready to install today.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: INK_MUTED,
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            Our Bass Hill workshop holds 150+ panels in primary Colorbond
            colours — white, primrose, paperbark, woodland grey, monument,
            surfmist, classic cream.
          </p>
        </div>

        {/* === BRAND GRID === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 0,
        border: `1px solid ${CREAM_DEEP}`,
        borderRadius: 4,
        overflow: "hidden",
        background: "#ffffff"
      }} className="panel-brands-grid">
          {BRANDS.map((b, i) => <BrandCard key={i} brand={b} index={i} />)}
        </div>

        {/* === BOTTOM STRIP === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "#ffffff",
        border: `1px solid ${CREAM_DEEP}`,
        borderLeft: `3px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: 8
          }}>
              Not sure what brand or colour?
            </div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: 19,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.3,
            letterSpacing: "-0.01em"
          }}>
              Send a photo — we'll identify the brand, profile, and colour
              within minutes.
            </div>
          </div>

          <a href="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20can%20you%20identify%20my%20garage%20door%20panel%3F" target="_blank" rel="noopener noreferrer" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          background: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "background 0.2s, transform 0.2s"
        }} onMouseEnter={e => {
          e.currentTarget.style.background = NAVY_LIGHT;
          e.currentTarget.style.transform = "translateY(-2px)";
        }} onMouseLeave={e => {
          e.currentTarget.style.background = NAVY;
          e.currentTarget.style.transform = "translateY(0)";
        }}>
            Send photo via WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .panel-brands-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 700px) {
          .panel-brands-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .panel-brands-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}
function BrandCard({
  brand,
  index
}: {
  brand: Brand;
  index: number;
}) {
  const isLastCol = (index + 1) % 3 === 0;
  const isLastRow = index >= 3;
  return <div className="panel-brand-card" style={{
    position: "relative",
    padding: "36px 32px",
    background: "#ffffff",
    borderRight: isLastCol ? "none" : `1px solid ${CREAM_DEEP}`,
    borderBottom: isLastRow ? "none" : `1px solid ${CREAM_DEEP}`,
    cursor: "default",
    transition: "background 0.3s",
    minHeight: 280,
    display: "flex",
    flexDirection: "column"
  }} onMouseEnter={e => {
    e.currentTarget.style.background = CREAM;
  }} onMouseLeave={e => {
    e.currentTarget.style.background = "#ffffff";
  }}>
      <div style={{
      fontFamily: FONT_HEAD,
      fontSize: 12,
      fontWeight: 800,
      color: GOLD_DARK,
      letterSpacing: "0.15em",
      marginBottom: 16
    }}>
        {String(index + 1).padStart(2, "0")} / {brand.spec}
      </div>

      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: 24,
      fontWeight: 800,
      color: INK,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      margin: "0 0 14px"
    }}>
        {brand.name}
      </h3>

      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: INK_MUTED,
      lineHeight: 1.55,
      margin: "0 0 24px",
      flex: 1
    }}>
        {brand.note}
      </p>

      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8
    }}>
        {brand.expertise.map((item, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: INK,
        fontWeight: 500
      }}>
            <CheckCircle2 size={14} color={GOLD_DARK} strokeWidth={2.5} style={{
          flexShrink: 0
        }} />
            {item}
          </li>)}
      </ul>
    </div>;
}